// useAuth — real session login through the API seam (US-882 · R-5/R-9).
//
// login(username, password) POSTs /auth/login/ (backend LoginView,
// map_stats/views.py): 200 { ok, username } authenticates, 401 { error } is
// bad credentials. The Django session cookie rides on useApi's
// credentials:"include"; server-side session/CSRF hardening is EPIC-18 (out
// of scope here). login resolves true/false for the credential outcome and
// re-throws any other ApiError (network down, 5xx) so Login.jsx can tell
// "wrong password" apart from "server unreachable" (R-8).
//
// This replaces the 2026-07-02 client-side demo gate. In fixtures mode
// (static demo, no backend) fixtures/auth.js answers this same contract,
// keeping the REACT_APP_DEMO_* gate semantics there instead of here.
import { useCallback, useState } from "react";
import useApi from "./useApi";
import endpoints from "../api/endpoints";
import { clear as clearApiCache } from "../api/cache";
// Side effect: registers the login contract fixture, so with
// REACT_APP_USE_FIXTURES the same call below resolves without a network
// (static demo / backend-less dev — US-883 mechanism).
import "../fixtures/auth";

const useAuth = () => {
  const api = useApi();
  const [isAuthed, setIsAuthed] = useState(false);

  const login = useCallback(
    async (username, password) => {
      try {
        const response = await api.post(endpoints.login.path, {
          username,
          password,
        });
        const ok = Boolean(response?.ok);
        if (ok) setIsAuthed(true);
        return ok;
      } catch (error) {
        if (error?.status === 401) return false; // bad credentials
        throw error; // network/5xx — the caller shows a distinct message
      }
    },
    [api]
  );

  // Client-side logout: drop the auth state and the client API cache so a
  // later login never reads the previous session's cached responses. The
  // server-side session lifecycle is EPIC-18.
  const logout = useCallback(() => {
    clearApiCache();
    setIsAuthed(false);
  }, []);

  return { isAuthed, login, logout };
};

export default useAuth;
